import { GM_STANDING } from "./standing.ts";

/**
 * GM roles and their capabilities (ADR-081 decision 5; `docs/backlog/gm-editor-mode.md`, "GM roles"). Access is a
 * set of capabilities, not a ladder: an account holds any number of roles, each role grants some capabilities,
 * and an account may do what the union of its live roles grants. A player holds no role and so can do nothing
 * here. The server checks every action against this table; an editor only uses it to decide what to show.
 *
 * ADR-081 fixes the building capabilities and the builder's, administrator's and lead's sets, and its amendment
 * of 2026-10-04 the support, moderator and event sets, which the moderation surface checks (ADR-056's amendment
 * of the same day). Its amendment of 2026-10-06 gives an administrator every capability of the roles below it but
 * the lead-only ones. Whom an account may act on is not a capability: it is standing (`standing.ts`).
 */

/**
 * The roles, in the spelling `account_roles.role` holds: today's seven, the five of ADR-112 decision 1.4, then the
 * Designer leader of its amendment of 2026-10-28. The check constraint of `account_roles` lists them all.
 */
export const GM_ROLES = ["support", "moderator", "administrator", "event", "builder", "designer", "lead", "guide", "guide_lead", "support_lead", "event_lead", "builder_lead", "designer_lead"] as const;

/**
 * One GM role.
 */
export type GmRole = (typeof GM_ROLES)[number];

/**
 * Every capability a role can grant, as a dotted name: the thing acted on, then the action.
 */
export const GM_CAPABILITY_NAMES = [
  "world.read",
  "world.save",
  "world.compile",
  "world.playtest",
  "world.publish",
  "world.rollback",
  "world.layout",
  "template.write",
  "preset.write",
  "brushset.write",
  "footprint.write",
  "studio.write",
  "studio.publish",
  "requests.send",
  "requests.send.game",
  "role.grant",
  "player.view",
  "report.read",
  "report.answer",
  "player.warn",
  "player.mute.short",
  "player.mute",
  "player.kick",
  "player.freeze",
  "player.flag",
  "player.message",
  "player.ban",
  "player.ban.permanent",
  "player.lift",
  "staff.designate",
  "event.spawn",
  "event.start",
  "zone.inspect",
] as const;

/**
 * One capability.
 */
export type GmCapability = (typeof GM_CAPABILITY_NAMES)[number];

const SUPPORT: readonly GmCapability[] = ["player.view", "report.read", "report.answer", "player.warn", "player.mute.short", "player.flag", "player.message", "requests.send.game", "zone.inspect"];
const MODERATOR: readonly GmCapability[] = ["player.view", "player.warn", "player.mute", "player.kick", "player.freeze", "player.ban", "player.lift", "player.flag", "player.message", "requests.send.game", "zone.inspect"];
const EVENT: readonly GmCapability[] = ["event.spawn", "event.start", "requests.send.game", "zone.inspect"];

const DESIGNING: readonly GmCapability[] = ["studio.write", "studio.publish", "requests.send", "requests.send.game", "zone.inspect"];

const BUILDING: readonly GmCapability[] = ["world.read", "world.save", "world.compile", "world.playtest", "template.write", "preset.write", "brushset.write", "footprint.write", "requests.send"];

/**
 * The capabilities no role but `lead` holds (ADR-081's amendment of 2026-10-06, item 3, and ADR-109 decision
 * 15): the permanent ban, applying the world layout plan, and designating which badge a staff account wears and reading
 * the staff accounts (the lead's staff screen, ADR-112 decision 5.5). The administrator's computed set leaves them out.
 * Frozen.
 */
export const LEAD_ONLY_CAPABILITIES: readonly GmCapability[] = Object.freeze<GmCapability[]>(["player.ban.permanent", "world.layout", "staff.designate"]);

/**
 * Every role's set but the administrator's, which is computed from them.
 */
const LISTED: Readonly<Record<Exclude<GmRole, "administrator">, readonly GmCapability[]>> = {
  support: Object.freeze<GmCapability[]>([...SUPPORT]),
  moderator: Object.freeze<GmCapability[]>([...MODERATOR]),
  event: Object.freeze<GmCapability[]>([...EVENT]),
  builder: Object.freeze<GmCapability[]>([...BUILDING, "requests.send.game", "zone.inspect"]),
  designer: Object.freeze<GmCapability[]>([...DESIGNING]),
  lead: Object.freeze<GmCapability[]>([...GM_CAPABILITY_NAMES]),
  guide: Object.freeze<GmCapability[]>(["player.flag", "requests.send.game"]),
  guide_lead: Object.freeze<GmCapability[]>(["player.flag", "requests.send.game", "player.message"]),
  support_lead: Object.freeze<GmCapability[]>([...new Set<GmCapability>([...SUPPORT, ...MODERATOR])]),
  event_lead: Object.freeze<GmCapability[]>([...EVENT]),
  builder_lead: Object.freeze<GmCapability[]>([...BUILDING, "requests.send.game", "zone.inspect"]),
  designer_lead: Object.freeze<GmCapability[]>([...DESIGNING]),
};

/**
 * The administrator's set (ADR-081's amendment of 2026-10-06 and its note on standing): its own capabilities, then
 * every capability of every role whose standing is below its own, in `GM_ROLES` order, less
 * `LEAD_ONLY_CAPABILITIES`. A capability later given to a lower role reaches the administrator with no edit here.
 *
 * @returns The set, each capability once, frozen.
 */
function administratorCapabilities(): readonly GmCapability[] {
  const held = new Set<GmCapability>([...BUILDING, "world.publish", "world.rollback", "role.grant", "requests.send.game", "zone.inspect"]);
  for (const role of GM_ROLES) {
    if (role !== "administrator" && GM_STANDING[role] < GM_STANDING.administrator) {
      for (const capability of LISTED[role]) {
        held.add(capability);
      }
    }
  }
  return Object.freeze([...held].filter((capability) => !LEAD_ONLY_CAPABILITIES.includes(capability)));
}

/**
 * Each role's capabilities. Support sees players and their reports, answers them, sends the Alert (`player.message`, ADR-056) and warns or mutes for a short
 * time; a moderator warns, sends the Alert, kicks, freezes, mutes and bans, and lifts them; event spawns event items and props and
 * starts event templates; a builder edits, compiles and play-tests draft worlds, templates, presets and footprints
 * and sends feedback from the hosted editor, but neither publishes nor rolls back (the author's ruling of
 * 2026-10-01); a designer works in the Asset Studio and publishes its packs (`studio.write`, `studio.publish`,
 * ADR-107 decision 31) and holds no capability of a builder's but `requests.send`, named in its own set so that a
 * designer can report a fault from the studio as a builder does from the map editor, and a builder holds none of the
 * studio's (the author's ruling of 2026-10-09: the two are strictly apart, so an account that is to do both holds
 * both roles); an
 * administrator publishes, rolls back and grants roles, and holds every capability of every role of
 * lower standing but the lead-only ones, so it bans for up to 30 days and never permanently, and works in the
 * Asset Studio as a designer does; a lead holds every
 * capability, and only a lead bans permanently (`LEAD_ONLY_CAPABILITIES`). ADR-112 adds a Guide, which flags a
 * player (`player.flag`) and sends feedback from the game and nothing more; a Guide leader, which holds those and
 * sends the Alert (`player.message`, ADR-056); a Player Support leader, which holds the support and moderator sets
 * and the Alert; and an Events, a Builder and (its amendment of 2026-10-28) a Designer leader, each holding its
 * member's set. Every role but a Guide and a Guide leader inspects the map server's performance in the game
 * (`zone.inspect`, ADR-087 decision 4), and every role sends feedback from the live game (`requests.send.game`,
 * ADR-081's amendment of 2026-10-03 on P1.9-T72).
 * The administrator's set is computed once, here. Frozen, so no caller widens a role at run time.
 */
export const GM_CAPABILITIES: Readonly<Record<GmRole, readonly GmCapability[]>> = Object.freeze({
  support: LISTED.support,
  moderator: LISTED.moderator,
  administrator: administratorCapabilities(),
  event: LISTED.event,
  builder: LISTED.builder,
  designer: LISTED.designer,
  lead: LISTED.lead,
  guide: LISTED.guide,
  guide_lead: LISTED.guide_lead,
  support_lead: LISTED.support_lead,
  event_lead: LISTED.event_lead,
  builder_lead: LISTED.builder_lead,
  designer_lead: LISTED.designer_lead,
});

const ROLE_SET: ReadonlySet<string> = new Set(GM_ROLES);

/**
 * Reports whether a string is one of the roles, as read from a database row or a command line.
 *
 * @param value The candidate.
 * @returns True for a role's exact lowercase name.
 */
export function isGmRole(value: string): value is GmRole {
  return ROLE_SET.has(value);
}

/**
 * The capabilities a set of roles grants together.
 *
 * @param roles The account's live roles, in any order, repeats allowed.
 * @returns The union, each capability once, sorted; empty for an account holding no role.
 */
export function capabilitiesOf(roles: readonly GmRole[]): GmCapability[] {
  const held = new Set<GmCapability>();
  for (const role of roles) {
    for (const capability of GM_CAPABILITIES[role]) {
      held.add(capability);
    }
  }
  return [...held].sort();
}

/**
 * The roles only a lead changes (ADR-112 decision 1.4): `administrator`, `lead` and the five leader roles. An
 * administrator, which holds `role.grant`, changes every other role. Frozen.
 */
export const LEAD_ONLY_ROLES: readonly GmRole[] = Object.freeze<GmRole[]>(["administrator", "lead", "guide_lead", "support_lead", "event_lead", "builder_lead", "designer_lead"]);

/**
 * Whether an account holding some roles may grant or revoke a role (ADR-081 decision 5): a lead may change any
 * role; an account with `role.grant` but not lead, an administrator, may change every role but those in
 * `LEAD_ONLY_ROLES`, so no administrator can make another administrator, a lead or a team leader, or take one
 * away; nobody else changes any. Every path that grants or revokes, the operator's command line and the GM surface of P1.4-T9,
 * checks this.
 *
 * @param acting The roles the acting account holds live.
 * @param role The role to grant or revoke.
 * @returns True when the change is allowed.
 */
export function mayChangeRole(acting: readonly GmRole[], role: GmRole): boolean {
  if (acting.includes("lead")) {
    return true;
  }
  return !LEAD_ONLY_ROLES.includes(role) && capabilitiesOf(acting).includes("role.grant");
}
